import React, { useContext, useState, useEffect } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles, createTheme, ThemeProvider } from "@material-ui/core/styles";
import { IconButton, CircularProgress } from "@material-ui/core";
import { MoreVert, Replay } from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import TicketOptionsMenu from "../TicketOptionsMenu";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import UndoRoundedIcon from '@material-ui/icons/UndoRounded';
import Tooltip from '@material-ui/core/Tooltip';
import { green } from '@material-ui/core/colors';
import TicketHistoryDialog from "../TicketHistoryDialog";



const useStyles = makeStyles(theme => ({
	actionButtons: {
		marginRight: 6,
		flex: "none",
		alignSelf: "center",
		marginLeft: "auto",
		"& > *": {
			margin: theme.spacing(0.5),
		},
	},
}));

const TicketActionButtonsCustom = ({ ticket }) => {
	const classes = useStyles();
	const history = useHistory();
	const [anchorEl, setAnchorEl] = useState(null);
	const [loading, setLoading] = useState(false);
	const ticketOptionsMenuOpen = Boolean(anchorEl);
	const { user } = useContext(AuthContext);
	const { setCurrentTicket } = useContext(TicketsContext);
	const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
	const [historyData, setHistoryData] = useState(null);
	const [loadingHistory, setLoadingHistory] = useState(false);
	const [historyError, setHistoryError] = useState(null);
	const [timeLeft, setTimeLeft] = useState(0);
	const [loadingRobot, setLoadingRobot] = useState(false);
	const [useRobotState, setUseRobotState] = useState(ticket.useRobot);


	const customTheme = createTheme({
		palette: {
			primary: green,
		}
	});

	const handleOpenTicketOptionsMenu = e => {
		setAnchorEl(e.currentTarget);
	};

	const handleCloseTicketOptionsMenu = e => {
		setAnchorEl(null);
	};

	const getHistory = async () => {
		setLoadingHistory(true);
		setHistoryError(null);
		try {
			const { data } = await api.get(`/tickets/${ticket.id}/history`);
			if (!data) {
				setHistoryError(`${data.error ? data.error : "Histórico não encontrado"}`);
				return;
			}
			setHistoryData(data);
			setHistoryDialogOpen(true);
		} catch (err) {
			setHistoryError("Erro ao consultar histórico");
			toastError(err);
		} finally {
			setLoadingHistory(false);
		}
	}

	useEffect(() => {
		setUseRobotState(ticket.useRobot);
	}, [ticket.useRobot]);

	useEffect(() => {
		let interval;

		if (useRobotState) {
			setTimeLeft(0);
			return;
		}

		let expiration = ticket.robotBlockExpiration ? parseInt(ticket.robotBlockExpiration) : null;
		if (!expiration && timeLeft > 0) {
			expiration = Date.now() + timeLeft;
		}

		const updateTimer = () => {
			if (expiration) {
				const now = Date.now();
				const diff = expiration - now;

				if (diff <= 0) {
					setTimeLeft(0);
					if (interval) clearInterval(interval);
					if (useRobotState === false) {
						handleUnblockRobot();
					}
				} else {
					setTimeLeft(diff);
				}
			} else {
				setTimeLeft(0);
			}
		};

		updateTimer();
		if (expiration) {
			interval = setInterval(updateTimer, 1000);
		}

		return () => {
			if (interval) clearInterval(interval);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ticket.robotBlockExpiration, useRobotState]);

	const formatTime = (ms) => {
		const seconds = Math.floor((ms / 1000) % 60);
		const minutes = Math.floor((ms / 1000 / 60) % 60);
		return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
	};

	const handleBlockRobot = async () => {
		setLoadingRobot(true);
		try {
			await api.post(`/robot/block/${ticket.id}`);
			setTimeLeft(300000); // Reinicia o tempo para 5 minutos imediatamente no clique
			setUseRobotState(false);
		} catch (err) {
			toastError(err);
		} finally {
			setLoadingRobot(false);
		}
	};

	const handleUnblockRobot = async () => {
		setLoadingRobot(true);
		try {
			await api.delete(`/robot/unblock/${ticket.id}`);
			setUseRobotState(true);
			setTimeLeft(0); // Zera o tempo imediatamente
		} catch (err) {
			toastError(err);
		} finally {
			setLoadingRobot(false);
		}
	};


	const handleUpdateTicketStatus = async (e, status, userId) => {
		setLoading(true);
		try {
			await api.put(`/tickets/${ticket.id}`, {
				status: status,
				userId: userId || null,
				useIntegration: status === "closed" ? true : ticket.useIntegration,
				promptId: status === "closed" ? false : ticket.promptId,
				integrationId: status === "closed" ? false : ticket.integrationId
			});

			setLoading(false);
			if (status === "open") {
				setCurrentTicket({ ...ticket, code: "#open" });
			} else {
				setCurrentTicket({ id: null, code: null })
				history.push("/tickets");
			}
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
	};

	return (
		<div className={classes.actionButtons} style={{ display: "flex", flexDirection: "row" }}>
			{ticket.status === "closed" && (
				<ButtonWithSpinner
					loading={loading}
					startIcon={<Replay />}
					size="small"
					onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
				>
					{i18n.t("messagesList.header.buttons.reopen")}
				</ButtonWithSpinner>
			)}
			{ticket.status === "open" && (
				<>
					{user.is_historico_active && (
						<ThemeProvider theme={customTheme}>
							{historyError && (
								<span style={{ color: "red", fontSize: "12px", marginLeft: "5px" }}>
									{historyError}
								</span>
							)}
							<IconButton onClick={getHistory} color="primary" disabled={loadingHistory}>
								{loadingHistory ? (
									<CircularProgress size={24} />
								) : (
									<span style={{ fontSize: "14px", color: "white", backgroundColor: "#0ea1cd", padding: "8px", borderRadius: "5px" }}>
										Historico
									</span>
								)}
							</IconButton>
						</ThemeProvider>
					)}
					{useRobotState ? (
						<Tooltip title="Pausar Robô">
							<IconButton onClick={handleBlockRobot} color="primary" disabled={loadingRobot}>
								{loadingRobot ? <CircularProgress size={24} /> : (
									<span style={{ fontSize: "14px", color: "white", backgroundColor: "#ff9800", padding: "8px", borderRadius: "5px" }}>
										Pausar Robô
									</span>
								)}
							</IconButton>
						</Tooltip>
					) : (
						<div style={{ display: "flex", alignItems: "center" }}>
							<Tooltip title="Acionar Robô">
								<IconButton onClick={handleUnblockRobot} color="primary" disabled={loadingRobot}>
									{loadingRobot ? <CircularProgress size={24} /> : (
										<span style={{ fontSize: "14px", color: "white", backgroundColor: "#4caf50", padding: "8px", borderRadius: "5px" }}>
											{"Acionar: " + (timeLeft > 0 ? formatTime(timeLeft) : "...")}
										</span>
									)}
								</IconButton>
							</Tooltip>
						</div>
					)}
					<Tooltip title={i18n.t("messagesList.header.buttons.return")}>
						<IconButton onClick={e => handleUpdateTicketStatus(e, "pending", null)}>
							<UndoRoundedIcon />
						</IconButton>
					</Tooltip>
					<ThemeProvider theme={customTheme}>
						<Tooltip title={i18n.t("messagesList.header.buttons.resolve")}>
							<IconButton onClick={e => handleUpdateTicketStatus(e, "closed", user?.id)} color="primary">
								<CheckCircleIcon />
							</IconButton>
						</Tooltip>
					</ThemeProvider>
					{/* <ButtonWithSpinner
						loading={loading}
						startIcon={<Replay />}
						size="small"
						onClick={e => handleUpdateTicketStatus(e, "pending", null)}
					>
						{i18n.t("messagesList.header.buttons.return")}
					</ButtonWithSpinner>
					<ButtonWithSpinner
						loading={loading}
						size="small"
						variant="contained"
						color="primary"
						onClick={e => handleUpdateTicketStatus(e, "closed", user?.id)}
					>
						{i18n.t("messagesList.header.buttons.resolve")}
					</ButtonWithSpinner> */}
					<IconButton onClick={handleOpenTicketOptionsMenu}>
						<MoreVert />
					</IconButton>
					<TicketOptionsMenu
						ticket={ticket}
						anchorEl={anchorEl}
						menuOpen={ticketOptionsMenuOpen}
						handleClose={handleCloseTicketOptionsMenu}
					/>
				</>
			)}
			{ticket.status === "pending" && (
				<ButtonWithSpinner
					loading={loading}
					size="small"
					variant="contained"
					color="primary"
					onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
				>
					{i18n.t("messagesList.header.buttons.accept")}
				</ButtonWithSpinner>
			)}
			<TicketHistoryDialog
				open={historyDialogOpen}
				onClose={() => setHistoryDialogOpen(false)}
				historyData={historyData}
			/>
		</div>
	);
};


export default TicketActionButtonsCustom;
